import 'react-datepicker/dist/react-datepicker.css'
import './DateRangeIndividualPicker.css'
import { ChevronUpDownIcon } from '@heroicons/react/24/outline'
import { getMonth } from 'date-fns'
import { ComponentPropsWithoutRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'

import { cn } from '@sparkai/utils'

import { DateInterval } from '../DatePicker/DatePicker'
import { DateRangeIndividualPickerHeader, RangeOptionButton } from './DateRangeIndividualPickerHeader'

export type DateRangeTuple = [Date | null, Date | null]

export interface DateRangeIndividualPickerProps {
  value?: DateRangeTuple
  onChange: (date: DateRangeTuple) => void
  placeholder?: string

  showTimeSelect?: boolean
  timeIntervals?: number
  monthsShown?: number
  showTimeInput?: boolean

  excludeDates?: Date[]
  excludeDateIntervals?: DateInterval[]
  filterDate?: (date: Date) => boolean

  excludeTimes?: Date[]
  filterTime?: (date: Date) => boolean

  shouldCloseOnSelect?: boolean
  showMonthAndYearSelector?: boolean
  timeFormat?: string
  dateFormat?: string

  maxDate?: Date | null
  minDate?: Date | null

  className?: string
  daysDefaultRange: number
  error?: boolean

  hideInput?: boolean
  autoShow?: boolean
  headerClassName?: string
  calendarTheme?: 'full' | 'shadow'
  inline?: boolean
  calendarClassName?: string
  rangeDaysOptions?: number[]
  referenceDate?: Date
}

export function DateRangeIndividualPicker({
  value,
  onChange,
  placeholder,
  daysDefaultRange,
  error,
  hideInput,
  autoShow,
  headerClassName,
  calendarTheme = 'shadow',
  calendarClassName,
  inline = false,
  rangeDaysOptions,
  referenceDate = new Date(),
  ...props
}: Readonly<DateRangeIndividualPickerProps>) {
  const startDate = value?.[0]
  const endDate = value?.[1]
  const datePickerRef = useRef<ReactDatePicker>(null)

  const [selectedRangeDays, setSelectedRangeDays] = useState<number | null>(null)
  const isRangeDaysSelected = selectedRangeDays !== null
  const [focusSelectedMonth, setFocusSelectedMonth] = useState(false)

  const todayMonthNumber = getMonth(referenceDate) + 1
  const startMonthNumber = startDate ? getMonth(startDate) + 1 : null
  const endMonthNumber = endDate ? getMonth(endDate) + 1 : null

  useEffect(() => {
    if (startMonthNumber !== todayMonthNumber && startMonthNumber !== null && endDate === null) {
      setFocusSelectedMonth(true)
    }
  }, [startMonthNumber, todayMonthNumber, endDate])

  const handleRangeSelect = (range: Date[], days: number) => {
    const date = range as unknown as DateRangeTuple
    setSelectedRangeDays(days)
    onChange?.(date)
    !autoShow && datePickerRef?.current?.setOpen(false)

    const s = date?.[0]
    const e = date?.[1]
    if (s && e) setFocusSelectedMonth(false)
  }

  return (
    <div className='date-range-individual-picker-root'>
      {rangeDaysOptions && (
        <div className='flex flex-col items-start gap-2 px-6 pt-6 font-inter'>
          <h2 className='text-base font-semibold'>Select a range of dates</h2>
          {rangeDaysOptions.map((days) => (
            <RangeOptionButton
              key={days}
              days={days}
              startDate={startDate}
              endDate={endDate}
              selectedRangeDays={selectedRangeDays}
              onSelect={handleRangeSelect}
              referenceDate={referenceDate}
            />
          ))}
          <div className='mt-2 h-[1px] w-full bg-[#E5E7EB]' />
        </div>
      )}
      <ReactDatePicker
        inline={inline}
        ref={datePickerRef}
        selected={startDate}
        showPopperArrow={false}
        customInput={
          hideInput ? (
            <></>
          ) : (
            <CustomInputSelect
              error={error}
              rangeDays={daysDefaultRange}
              isRangeDaysSelected={isRangeDaysSelected}
            />
          )
        }
        calendarClassName={cn(
          {
            'w-full border-0 mt-[-20px]': calendarTheme === 'full',
            'shadow-xl ring ring-black/5 p-6 border-0 border-black/5 rounded-md': calendarTheme === 'shadow',
          },
          calendarClassName
        )}
        onChange={(update) => {
          if (onChange) {
            const date = update as unknown as DateRangeTuple
            setSelectedRangeDays(null)

            const startDate = date?.[0]
            const endDate = date?.[1]

            if (startDate && endDate) setFocusSelectedMonth(false)

            onChange(date)
          }
        }}
        startDate={startDate}
        renderCustomHeader={(props) => (
          <DateRangeIndividualPickerHeader
            className={headerClassName}
            onClickRangeDates={(value) => {
              const date = value as unknown as DateRangeTuple
              setSelectedRangeDays(daysDefaultRange)
              onChange && onChange(date)
              !autoShow && datePickerRef?.current?.setOpen(false)

              const startDate = date?.[0]
              const endDate = date?.[1]

              if (startDate && endDate) setFocusSelectedMonth(false)
            }}
            rangeDaysSelected={isRangeDaysSelected}
            rangeDays={daysDefaultRange}
            startDate={startDate}
            endDate={endDate}
            externalRangeOptions={!!rangeDaysOptions}
            referenceDate={referenceDate}
            {...props}
          />
        )}
        monthsShown={2}
        showPreviousMonths={
          (!startDate && !endDate) ||
          (autoShow && todayMonthNumber === startMonthNumber && todayMonthNumber === endMonthNumber)
        }
        focusSelectedMonth={focusSelectedMonth}
        endDate={endDate}
        placeholderText={placeholder}
        selectsRange
        open={autoShow}
        onCalendarClose={() => {
          setFocusSelectedMonth(false)
        }}
        {...props}
      />
    </div>
  )
}

export type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  isRangeDaysSelected?: boolean
  rangeDays: number
  error?: boolean
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { value, isRangeDaysSelected, error, rangeDays, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // Just a trick to use ref while also forwarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  return (
    <button
      ref={innerRef}
      type='button'
      {...props}
      className={cn(
        'mt-1 flex w-full cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 font-inter text-sm text-gray-900 shadow-xs',
        {
          'border border-red-400': error,
        }
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        innerRef.current?.focus()
      }}
      aria-label='open calendar modal'
    >
      <span>
        {value}
        {isRangeDaysSelected && <span className='ml-1 text-gray-500'>(Last {rangeDays} Days)</span>}
      </span>

      <ChevronUpDownIcon className='h-5 w-5 font-bold text-gray-400' aria-hidden='true' />
    </button>
  )
})
